import { Layouts } from 'react-grid-layout';

import { CreateUserData } from '../api/types';
import { DOCUMENT_STATUSES, RTW_VALUES } from '../constants';
import type { Address, ChangeLog, Language, Nationality, Photo, Tag } from './common';
import type { Company, CompanyDTO } from './company';
import type { AppNotification } from './common';
import type { QualificationsDTO } from './qualification';
import type { UserPermission } from './permission';

export interface AuthFormValues {
  email: string;
  first_name: string;
  last_name: string;
  password_1: string;
  password_2: string;
  phone?: string;
  rememberMe?: boolean;
}

export interface Worker {
  uuid: string;
  biography: string;
  search_status: string;
  tags: Tag[];
  languages: Language[];
  address: Address;
  positions: Position[];
  qualifications: any;
  english_level?: string;
  own_ppe?: boolean;
  own_vehicle?: boolean;
  own_tools?: boolean;
  own_uk_driving_license?: boolean;
  travel_time?: number;
  available?: boolean;
  id_verified?: boolean;
  rtw_verified?: boolean;
  cscs_verified?: boolean;
  cpcs_verified?: boolean;
  eusr_verified?: boolean;
}

export interface CompanyPosition {
  company_id: string;
  company: Company;
  role: string;
  owner: boolean;
  naive_check: boolean;
  /**
   * Every right the position holds company-wide — menus and pages, but action
   * rights too (time:can_update_sites and the like). The single company-level
   * source: company_actions no longer exists and its contents arrive here.
   * Anything narrower than the whole company lives in `resources`.
   */
  permissions: UserPermission[];
  /** resource_type -> resource_id -> effective actions. Owners get {} on purpose. */
  resources?: Record<string, Record<string, string[]>>;
}

// Companies the user can reach but where they may hold no staff position.
export interface UserCompanyAccess {
  company_id: string;
  access_to: Record<string, unknown>;
}

export interface User {
  uuid: string;
  gender?: string | null;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string | null;
  positions?: CompanyPosition[];
  access_to?: UserCompanyAccess[];
  nationality?: Nationality;
  photo?: Photo;
  address?: Address;
  worker?: Worker;
  employee?: Employee;
  context?: {
    ni_number: string;
    eusr_id?: string;
    cscs_id?: string;
    cscs_ref_number?: string;
    share_code?: string;
    rtw_share_code?: string;
    rtw_verified?: boolean;
    cscs_verified?: boolean;
    cpcs_verified?: boolean;
    eusr_verified?: boolean;
  };
  is_superuser?: boolean;
  is_staff?: boolean;
  email_verified?: boolean;
  phone_verified?: boolean;
  qualifications?: any;
  id_verification?: IDVerification;
  rtw_verification?: RTWVerification;
  dashboards?: UserDashboards;
  roles?: RolesDTO;
  ni_number?: string;
  isCompanyAdmin?: boolean;
  is_deleted?: boolean;
  onboarding?: import('./onboarding').UserOnboardingStatus | null;
}

export interface RolesDTO {
  user_id: string;
  roles_by_companies: {
    company_name: string;
    roles: RoleItem[];
  }[];
}

export interface RoleItem {
  module: string;
  role: string;
}

export interface UserDashboard {
  widgets?: import('./dashboard').WidgetItem[];
  layouts?: Layouts;
}

export interface UserDashboards {
  general?: UserDashboard;
  connect?: UserDashboard;
  teams?: UserDashboard;
  pay?: UserDashboard;
  time?: UserDashboard;
}

export interface UserPreviewData {
  user: User;
  surveys?: import('./survey').UserSurvey[];
  qualifications?: QualificationsDTO;
}

export interface Position {
  title: any;
  company: Company;
  start_date: string;
  uuid: string;
  end_date: string | null;
  currently_active: boolean;
}

export interface RequestWorkerData {
  address?: Address;
  biography?: string;
  tags?: Tag[];
  languages?: Language[];
  positions?: Position[];
  search_status?: string;
  travel_time?: number | null;
}

export interface RequestPositionDataData {
  role: string;
  company: Company;
}

export interface PersonalForm {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: string | Date | null;
  nationality: Nationality;
  address: Address;
  languages: Language[];
  ni_number: string;
  cscs_ref_number: string;
  utrNumber: string;
  share_code: string;
  gender: { name: string; value: string };
}

export interface WorkerProfessionalForm {
  biography: string;
  roles: Tag[];
  skills: Tag[];
  search_status: Tag;
}

export interface WorkerOnboardingForm {
  address: Address;
  workerAddress: string;
  jobs: Tag[];
  ni_number: string;
  nationality: Nationality;
  date_of_birth: any;
  qualifications: Tag[];
  languages: Language[];
  specific_skills: Tag[];
  positions: Position[];
  search_status: string;
  travel_time: string;
}

interface CandidateHealthAndSafety {
  status: boolean;
  total: number;
  answered: number;
}

export interface Candidate {
  user_id: string;
  worker_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  photo: Photo;
  gender: string;
  date_of_birth: string;
  nationality: Nationality;
  qualifications?: any;
  address: Address;
  biography: string;
  tags: Tag[];
  languages: Language[];
  positions: any[];
  search_status: string;
  id_verified: boolean;
  rtw_verified: boolean;
  cscs_verified: boolean;
  cpcs_verified: boolean;
  eusr_verified: boolean;
  health_and_safety: CandidateHealthAndSafety;
  computed: {
    connection: import('./connect').Connection;
    distance: {
      value: number;
      type: string;
    };
  };
}

export interface Passport {
  uuid: string;
  passport_type: string;
  additional_doc_type?: string;
  country: string;
  number: string;
  date_of_birth: string;
  expiration_date: string;
  nationality: string;
  gender: string;
  first_name: string;
  last_name: string;
  verified: boolean;
  document: import('./qualification').Document;
  selfie: import('./qualification').Document;
}

export interface IDVerification {
  expiration_date: string;
  status: DOCUMENT_STATUSES;
  user_id: string;
  uuid: string;
  verified: boolean;
}

export interface RTWVerification {
  expiration_date: string | null;
  check_date: string | null;
  status: RTW_VALUES;
  user_id: string;
  uuid: string;
  verified: boolean;
}

export interface RTWProps {
  uuid: string;
  full_name: string;
  check_date: string;
  ref_number: string;
  company_name: string;
  permanent: boolean;
  expired: boolean;
  user_id: string;
  document: import('./qualification').Document;
  photo: {
    uuid: string;
    title: string;
    doc_type: string;
    url: string;
  };
}

export interface AdditionalDocument {
  title: string;
  doc_type: string;
  description: string;
  uuid: string;
  user_id: string;
  document: {
    uuid: string;
    title: string;
    doc_type: string;
    file_name: string;
    url: string;
  };
}

export interface CreateUser {
  data: CreateUserData;
  redirectUrl?: string;
  saveToStore?: boolean;
  recaptcha_token: string;
  handleServerError: (e: any) => void;
}

export interface VerifyPhoneData {
  code: number;
  redirectUrl?: string;
  skip?: boolean;
  handleServerError: (e: any) => void;
}

export interface LoginData {
  username: string;
  password: string;
  redirectUrl?: string;
  recaptcha_token: string;
  handleServerError: (e: any) => void;
  showNotification: (notification: AppNotification) => void;
}

export interface UpdateIAMUserData {
  first_name: string;
  last_name: string;
  gender: string | null;
  date_of_birth: string | null;
  ni_number: string;
  nationality_id: string | null;
  cscs_ref_number: string | null;
}
export interface UpdateUser {
  data: UpdateIAMUserData;
  isNotify?: boolean;
  callback?: Function;
  showNotification: (notification: AppNotification) => void;
  handleServerError: (e: any) => void;
}
export interface UploadAvatarData {
  file: File;
  saveToProfile: boolean;
  handleServerError: (e: any) => void;
}
export interface UpdateWorkerData {
  data: RequestWorkerData;
  showNotification: (notification: AppNotification) => void;
  handleServerError: (e: any) => void;
}

export interface UpdateProfilePhotoData {
  photo_url: string;
  avatar_url: string;
  file_name: string;
}

export interface BankAccount {
  account_name: string;
  account_number: string;
  sort_code: string;
  building_society_roll_number?: string | null;
}

export interface Employee {
  job_title: string | Tag;
  email?: string;
  phone?: string;
  pay_schedule: any;
  first_name: string;
  last_name: string;
  gender?: string | null;
  nationality?: Nationality;
  date_of_birth?: string;
  region?: string;
  ni_number: string;
  cscs_id?: string;
  effective_date: string;
  trading_name: string;
  utr: string;
  cis_taxation_status?: string;
  display_cis_taxation_status?: string;
  cis_verification_date?: string;
  cis_verification_number?: string;
  business_type: string;
  vat_reg_number: string;
  vat_registered?: boolean;
  payment_method: string;
  bank_account?: BankAccount | null;
  address?: Address;
  employee_id: number;
  worker_reference: string;
  scheme_type: string;
  company_name?: string;
  company_reg_number?: string;
  company_utr?: string;
  uuid: string;
  user_id: string;
  pay_scheme: string;
  id_verified: boolean;
  rtw_verified: boolean;
  cis_verified: boolean;
  emergency_contact_name: string;
  emergency_contact_number: string;
  company?: CompanyDTO;
  is_agency_worker?: boolean;
  agency_ref_number?: string;
  logs: ChangeLog[];
}

export interface EmployeePaySchedule {
  company: CompanyDTO;
  pay_schedule: {
    pay_schedule_id: string;
    name: string;
  };
  pay_scheme: string;
  start_date: string;
  uuid?: string;
}

export interface InvitedUser {
  email: string;
  phone: string;
  company_id: string;
  user_id: string;
  status: string;
  payload: { [key: string]: unknown[] };
}

// Row of the invites search endpoints (/public/v1/invites/companies/{id}/search
// and /admin/v1/invites/search). user_id is null until the invitee has a Vyce account —
// in that case onboarding is null for every module ("Not started").
export interface InviteListItem {
  uuid: string;
  company_id: string;
  user_id?: string | null;
  full_name?: string | null;
  gender?: string | null;
  avatar?: string | null;
  email?: string | null;
  phone?: string | null;
  company_name?: string | null;
  modules?: string | string[] | null;
  invite_type?: string | null;
  created_by_full_name?: string | null;
  created_at?: string | null;
  status?: string | null;
  onboarding?: import('./onboarding').WorkerOnboarding | null;
  // Teams → Payroll → Time & Attendance, oldest first. Empty for staff invites and
  // until IAM and the gateway serve items — "no history yet", not an error.
  items?: InviteItem[];
}

// Envelope status. partially_accepted: some places worked, others failed;
// cancelled: replaced by a newer invite for the same person — final, cannot be resent.
export type InviteStatus =
  | 'pending'
  | 'sent'
  | 'not_sent'
  | 'accepted'
  | 'not_accepted'
  | 'expired'
  | 'automatically_accepted'
  | 'partially_accepted'
  | 'cancelled';

export type InviteItemResourceType = 'team' | 'location' | 'pay_schedule';

// added / already_in are places settled without an invite (a direct add)
export type InviteItemStatus =
  | 'pending'
  | 'sent'
  | 'not_sent'
  | 'accepted'
  | 'auto_accepted'
  | 'added'
  | 'already_in'
  | 'failed'
  | 'expired'
  | 'cancelled';

export type InviteItemReason =
  | 'new_account'
  | 'new_to_company'
  | 'new_to_module'
  | 'details_missing'
  | 'details_stale'
  | 'no_recent_clock_in'
  | 'no_payroll_record'
  | 'manual_resend'
  | 'legacy'
  | 'unknown';

export type InviteItemSource =
  | 'invite_dialog'
  | 'legacy_employees_route'
  | 'public_link'
  | 'silent_add'
  | 'backfill';

// One place of an invite (InviteItemRead): a team, location or pay schedule, who placed it and
// why, how the person was told and how it ended. Timestamps are UTC without a timezone suffix.
export interface InviteItem {
  uuid: string;
  module: 'teams' | 'payroll' | 'time';
  resource_type: InviteItemResourceType;
  resource_id: string;
  // The name when invited; null once the resource is deleted
  resource_name?: string | null;
  // false: the name was looked up later (an old invite), so it is today's name
  name_snapshot_original?: boolean;
  pay_scheme?: string | null;
  shift_id?: string | null;
  status: InviteItemStatus;
  // null for added / already_in
  reason?: InviteItemReason | null;
  // app, sms, email, or several joined by "+"; null until the person was told
  channel?: string | null;
  failure_reason?: string | null;
  source: InviteItemSource;
  invited_by?: string | null;
  invited_by_full_name?: string | null;
  invited_at: string;
  processed_at?: string | null;
  created_at: string;
}

export interface UserManagementItem {
  user_id: string;
  ni_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  avatar: string;
}

export interface RecentlyRegisteredDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  avatar: string;
  ni_number: string;
  registered_at: string;
  gender: string;
}

export interface CompanyWorkerItem {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar: string | null;
  ni_number: string | null;
  modules: string[];
  email: string | null;
  phone: string | null;
  onboarding?: import('./onboarding').WorkerOnboarding | null;
}

export interface StaffListItem {
  user_id: string;
  first_name: string;
  last_name: string;
  gender: string;
  email: string;
  phone: string;
  created_at: string;
  owner: boolean;
  modules: Record<string, string>;
  resources: Record<string, string[]>;
  badges: string[];
  photo?: {
    url: string;
  };
  ni_number?: string;
  avatar?: string;
}

export interface CSCSParams {
  cscs_id?: string;
  ni_number?: string;
  last_name: string;
}

export enum USER_BADGES {
  MOBILE_APP_ADMIN_TUTORIAL_COMPLETED = 'mobile_app_admin_tutorial_completed',
}
